import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api.config';
import './PaymentSuccess.css';

interface SystemSettings {
  phone?: string;
  email?: string;
  companyName?: string;
}

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(20);
  const [settings, setSettings] = useState<SystemSettings>({
    phone: '+49 170 7744661',
    email: 'info@frankenfloss.de'
  });

  // Get booking details from location state or query params
  const state = location.state as {
    bookingNumber?: string;
    amount?: number;
    paymentMethod?: string;
  } | null;

  const bookingNumber = state?.bookingNumber
    || searchParams.get('booking')
    || '-';

  const amount = state?.amount
    || parseFloat(searchParams.get('amount') || '0')
    || 0;

  const paymentMethod = state?.paymentMethod
    || searchParams.get('method')
    || '';

  // Format currency for German locale
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  useEffect(() => {
    // Load system settings for contact info
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/settings`);
        if (response.ok) {
          const data = await response.json();
          setSettings({
            phone: data.phone || '+49 170 7744661',
            email: data.email || 'info@frankenfloss.de',
            companyName: data.companyName
          });
        }
      } catch (error) {
        console.log('Could not load settings, using defaults');
      }
    };
    fetchSettings();

    // Auto-redirect countdown
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          navigate('/dashboard');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleDownloadInvoice = async () => {
    try {
      const token = localStorage.getItem('authToken');

      // First, get the booking ID from the booking number
      const bookingsResponse = await fetch(`${API_BASE_URL}/user/bookings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!bookingsResponse.ok) {
        alert('Fehler beim Laden der Buchungsdaten');
        return;
      }

      const bookingsData = await bookingsResponse.json();
      console.log('Bookings data:', bookingsData);

      // Handle different response formats
      let bookingsList = [];
      if (Array.isArray(bookingsData)) {
        bookingsList = bookingsData;
      } else if (bookingsData.items && Array.isArray(bookingsData.items)) {
        bookingsList = bookingsData.items;
      } else if (bookingsData.bookings && Array.isArray(bookingsData.bookings)) {
        bookingsList = bookingsData.bookings;
      } else if (bookingsData.data && Array.isArray(bookingsData.data)) {
        bookingsList = bookingsData.data;
      }

      const booking = bookingsList.find((b: any) => b.bookingNumber === bookingNumber);

      if (!booking) {
        console.log('Booking not found. Looking for:', bookingNumber, 'in', bookingsList.map((b: any) => b.bookingNumber));
        alert('Buchung nicht gefunden. Die Rechnung wird Ihnen per E-Mail zugesendet.');
        return;
      }

      console.log('Found booking:', booking);

      // Get invoices for this booking
      const invoicesResponse = await fetch(`${API_BASE_URL}/invoices/booking/${booking.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!invoicesResponse.ok) {
        console.log('Invoices response not ok:', invoicesResponse.status);
        alert('Die Rechnung wird Ihnen in Kürze per E-Mail zugesendet.');
        return;
      }

      const invoices = await invoicesResponse.json();
      console.log('Invoices:', invoices);

      // Handle different response formats for invoices
      let invoicesList = [];
      if (Array.isArray(invoices)) {
        invoicesList = invoices;
      } else if (invoices.invoices && Array.isArray(invoices.invoices)) {
        invoicesList = invoices.invoices;
      } else if (invoices.data && Array.isArray(invoices.data)) {
        invoicesList = invoices.data;
      }

      if (!invoicesList || invoicesList.length === 0) {
        alert('Die Rechnung wird derzeit erstellt und Ihnen per E-Mail zugesendet.');
        return;
      }

      // Get the most recent invoice
      const invoice = invoicesList[0];
      console.log('Downloading invoice:', invoice);

      // Download PDF
      const pdfResponse = await fetch(`${API_BASE_URL}/invoices/${invoice.id}/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!pdfResponse.ok) {
        alert('Fehler beim Herunterladen der Rechnung. Sie erhalten diese per E-Mail.');
        return;
      }

      const blob = await pdfResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoiceNumber || 'Rechnung'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Die Rechnung wird Ihnen per E-Mail zugesendet.');
    }
  };

  const handleViewBooking = () => {
    navigate('/dashboard');
  };

  return (
    <div className="payment-success-container">
      <div className="success-card">
        <div className="success-icon">
          <div className="checkmark-outer-circle">
            <div className="checkmark-circle">
              <svg viewBox="0 0 52 52" width="60" height="60">
                <circle cx="26" cy="26" r="25" fill="#10b981"/>
                <path
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 27l7 7 15-15"
                />
              </svg>
            </div>
          </div>
        </div>

        <h1>{paymentMethod === 'bank_transfer' ? 'Buchung bestätigt!' : 'Zahlung erfolgreich!'}</h1>
        <div className="success-message-box">
          <span className="success-checkmark">✓</span>
          <p>
            {paymentMethod === 'bank_transfer'
              ? 'Vielen Dank! Ihre Buchung wurde bestätigt. Bitte überweisen Sie den Betrag innerhalb von 7 Tagen.'
              : 'Vielen Dank für Ihre Anzahlung. Ihre Buchung wurde bestätigt.'}
          </p>
        </div>

        <div className="payment-details">
          <div className="detail-box">
            <span className="detail-label">Buchungsnummer</span>
            <span className="detail-value">{bookingNumber}</span>
          </div>
          <div className="detail-box">
            <span className="detail-label">{paymentMethod === 'bank_transfer' ? 'Zu überweisender Betrag' : 'Gezahlter Betrag'}</span>
            <span className="detail-value amount">{formatCurrency(amount)}</span>
          </div>
        </div>

        <div className="next-steps">
          <h3>Nächste Schritte:</h3>
          <ul>
            <li>
              <span className="step-icon">📧</span>
              <div>
                <strong>Bestätigungs-E-Mail</strong>
                <p>Sie erhalten in Kürze eine E-Mail mit allen Details</p>
              </div>
            </li>
            <li>
              <span className="step-icon">🍽️</span>
              <div>
                <strong>35 Tage vor der Fahrt</strong>
                <p>Erinnerung zur Essensauswahl für Ihre Gruppe</p>
              </div>
            </li>
            <li>
              <span className="step-icon">📅</span>
              <div>
                <strong>25 Tage vor der Fahrt</strong>
                <p>Sie erhalten die Schlussrechnung per E-Mail</p>
              </div>
            </li>
            <li>
              <span className="step-icon">🎫</span>
              <div>
                <strong>Nach Zahlung der Schlussrechnung</strong>
                <p>Sie erhalten Ihr Bordticket mit Anfahrtsskizze</p>
              </div>
            </li>
          </ul>
        </div>

        <div className="action-buttons">
          <button 
            className="btn-primary"
            onClick={handleViewBooking}
          >
            Zur Buchungsübersicht
          </button>
          <button 
            className="btn-secondary"
            onClick={handleDownloadInvoice}
          >
            Rechnung herunterladen
          </button>
        </div>

        <div className="auto-redirect">
          <p>Sie werden in <strong>{countdown}</strong> Sekunden automatisch weitergeleitet...</p>
        </div>

        <div className="support-info">
          <p>Haben Sie Fragen? Kontaktieren Sie uns:</p>
          <div className="contact-options">
            <a href={`tel:${settings.phone?.replace(/\s/g, '')}`}>📞 {settings.phone}</a>
            <a href={`mailto:${settings.email}`}>✉️ {settings.email}</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
